import { useEffect } from 'react';
import { showNotificationToast, type SweetAlertIcon } from '@/helpers/alertHelper';
import { useDispatch, useSelector } from 'react-redux';
import { formatIsoDatesInTextBR } from '@/lib/date';
import api from '@/services/api';
import { IRootState } from '@/store';
import { markRead as markReadAction, markAllRead, pushNew, setList } from '@/store/notificationsSlice';
import type { Notification } from '@/store/notificationsSlice';
import { useChannel } from '@/realtime/useChannel';

/**
 * Normalises a raw Laravel database notification into our Redux Notification shape.
 * Laravel returns:
 *   { id, type, data: { title, body, url, toast, icon, data }, read_at, created_at }
 * We flatten `data` up and camelCase the timestamps.
 */
interface RawNotificationPayload {
    id?: string;
    title?: string;
    body?: string;
    message?: string;
    url?: string;
    link?: string;
    icon?: string;
    toast?: boolean;
    extra?: Record<string, unknown>;
    created_at?: string;
    read_at?: string | null;
    data?: {
        title?: string;
        body?: string;
        message?: string;
        url?: string;
        link?: string;
        icon?: string;
        toast?: boolean;
        data?: Record<string, unknown>;
    };
}

const TOAST_ICONS: SweetAlertIcon[] = ['success', 'error', 'warning', 'info', 'question'];

function isSweetAlertIcon(value?: string): value is SweetAlertIcon {
    return value != null && TOAST_ICONS.includes(value as SweetAlertIcon);
}

function normalize(raw: RawNotificationPayload): Notification {
    const d = raw.data ?? {};
    return {
        id: raw.id ?? crypto.randomUUID(),
        title: d.title ?? raw.title ?? '',
        body: d.body ?? d.message ?? raw.body ?? raw.message ?? undefined,
        url: d.url ?? d.link ?? raw.url ?? raw.link ?? undefined,
        icon: d.icon ?? raw.icon ?? undefined,
        toast: d.toast ?? raw.toast ?? false,
        data: d.data ?? raw.extra ?? undefined,
        createdAt: raw.created_at ?? new Date().toISOString(),
        readAt: raw.read_at ?? null,
    };
}

function showToast(n: Notification) {
    showNotificationToast(n.title, {
        icon: isSweetAlertIcon(n.icon) ? n.icon : 'info',
        text: n.body ? formatIsoDatesInTextBR(n.body) : undefined,
    });
}

export function useUserNotifications() {
    const dispatch = useDispatch();
    const userId = useSelector((s: IRootState) => s.auth.user?.id ?? null);

    // Fetch on mount / when userId changes
    useEffect(() => {
        if (!userId) return;
        api.get('/api/notifications')
            .then((r) => {
                const list: Notification[] = (r.data.data ?? []).map(normalize);
                dispatch(setList(list));
            })
            .catch(() => {
                /* silently fail */
            });
    }, [userId, dispatch]);

    // Subscribe to real-time channel
    useChannel(userId ? `user.${userId}` : null, {
        'notification.created': (payload) => {
            const n = normalize((payload ?? {}) as RawNotificationPayload);
            dispatch(pushNew(n));
            if (n.toast) showToast(n);
        },
        'system.ping': () => {
            /* debug — no-op */
        },
    });

    const markAsRead = async (id: string) => {
        try {
            await api.patch(`/api/notifications/${id}/read`);
            dispatch(markReadAction(id));
        } catch {
            /* silently fail */
        }
    };

    const markAllAsRead = async () => {
        try {
            await api.patch('/api/notifications/read-all');
            dispatch(markAllRead());
        } catch {
            /* silently fail */
        }
    };

    return { markAsRead, markAllAsRead };
}
